import React, { useState, useEffect, useCallback, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import api, {
    fetchCostCategories,
    fetchNewCostQuantityTypes,
    addNewCo2Weight,
    updateCo2Weight,
    fetchNewMaterialCategories,
    addTestData,
} from '../services/api';
import './NabersRefEfPage.css';
import NabersImportModal from './NabersImportModal';
import PageJumpInput from './PageJumpInput';
import BulkGenerateModal from './BulkGenerateModal';

// ─── Material Type → EF Match mapping (from NABERS reference data) ────────────
const EF_MATCH_BY_MATERIAL_TYPE = {
    'Concrete in-situ': [
        '≤10 MPa', '>10 MPa to ≤20 MPa', '>20 MPa to ≤25 MPa', '>25 MPa to ≤32 MPa',
        '>32 MPa to ≤40 MPa', '>40 MPa to ≤50 MPa', '>50 MPa to ≤65 MPa',
        '>65 MPa to ≤80 MPa', '>80 MPa +', 'Kerb',
    ],
    'Concrete precast panel': [
        'Precast concrete panel (inc. reinforcing)',
        'Hollow core - precast concrete (inc. reinforcing)',
    ],
    'Concrete block': [
        'Concrete block (exc. core fill or reinforcing)',
        'Solid AAC (no fill or reinforcing)',
    ],
    'Reinforcing steel': [
        'Bar & mesh reinforcing steel',
        'Fibre & strand reinforcing steel',
    ],
    'Structural steel': [
        'Structural sections steel (welded beam, columns, angles, plates, piles etc) (hot rolled)',
        'Structural formwork and decking steel (cold rolled - light weight)',
        'Structural framing steel (cold rolled - lightweight)',
        'Galvanised structural sections steel (welded beam, columns, angles, plates, piles etc.) (hot rolled)',
        'Painted structural sections steel (welded beam, columns, angles, plates, piles etc.) (hot rolled)',
    ],
    'Stainless steel': ['Stainless steel (general)'],
    'Timber': ['Softwood', 'Hardwood', 'Timber poles - piles'],
    'Timber (engineered)': ['Plywood', 'Particleboard', 'GLT & CLT (softwood)', 'GLT & CLT (hardwood)', 'LVL', 'OSB', 'MDF'],
    'Brick': ['Clay brick', 'Concrete brick', 'Stone brick'],
    'Aggregate': ['Quarried fill and base material', 'Recycled fill material', 'Stabilised sand'],
    'Asphalt': ['Asphalt'],
    'Roof tiles': ['Clay and terracotta roof tiles', 'Concrete roof tiles'],
    'Steel cladding/roofing': ['Steel sheeting - metallic coat', 'Steel sheeting - painted'],
    'Aluminium cladding/roofing': ['Aluminium sheeting'],
    'Cement cladding/roofing': ['GRC', 'Fibre cement board', 'Plasterboard'],
    'Plastic cladding/roofing': ['Plastic sheeting'],
    'Timber cladding': ['Timber weatherboards', 'Plywood', 'Particleboard'],
    'Structural insulated panel': ['Panel - SIP ≤100mm', 'Panel - SIP >100mm'],
    'Glazing': ['Glass (treated or untreated)'],
    'Extruded aluminium': [
        'Aluminium extruded uncoated',
        'Aluminium extruded powder coated',
        'Aluminium extruded anodised',
    ],
    'External shading': ['Aluminium louvre', 'Aluminium construction', 'Steel construction'],
    'Ceilings & walls': [
        'Steel frame', 'Timber frame', 'AAC panel (inc. reinforced)', 'Plasterboard',
        'Ceiling tile', 'Particleboard wall/ceiling', 'MDF', 'Fibre cement sheet', 'Glass',
        'Wall system steel frame - 1 side plasterboard (92mm stud)',
        'Wall system steel frame - 2 sides plasterboard (92mm stud)',
        'Wall system steel frame - 2 sides plasterboard (150 mm stud)',
        'Wall system steel frame - 1 side plasterboard (150 mm stud)',
        'Wall system timber frame - 2 sides plasterboard',
        'Wall system timber frame - 1 side plasterboard',
        'AAC and steel based system - generic',
        'Ceiling system steel frame suspended (plasterboard)',
        'Ceiling system steel frame suspended (fibre cement)',
        'Ceiling system steel frame suspended (mineral tile)',
        'Ceiling system aluminum frame suspended (powder coated aluminium)',
    ],
    'Pavers/tiles': ['Concrete paver/tile', 'Ceramic paver/tile', 'Stone paver/tile'],
    'Floors': [
        'Access flooring', 'Carpet flooring', 'Vinyl flooring', 'Rubber flooring',
        'Timber flooring', 'Laminate flooring', 'Hybrid flooring', 'Linoleum flooring',
        'Epoxy flooring',
    ],
    'Roller doors': ['Roller doors'],
    'Windows & doors': [
        'Window - aluminium frame - generic',
        'Window - timber frame - generic',
        'Door - timber with glazing - generic',
        'Door - steel with glazing - generic',
        'Door - fully glazed - minor aluminium frame - generic',
        'Door - aluminium with glazing - generic',
        'Door - timber solid - generic',
        'Door - steel solid - generic',
        'Revolving door - glass, aluminium, steel - generic',
    ],
    'Curtain wall': [
        'Aluminium unitised type 1: 100% DGU 8T-16-44.2',
        'Aluminium unitised type 2: aluminium cladding, 50% DGU 8T-16-44.2',
        'Aluminium unitised type 3: aluminium cladding with vertical fins, 50% DGU 8T-16-44.2',
        'Aluminium unitised type 4: aluminium cladding, 50% TGU 8T-16-6-16-44.2',
        'Aluminium unitised type 5: insulated shadow box, 50% DGU 8T-16-44.2',
        'Aluminium unitised type 6: GRC cladding, 50% DGU 8T-16-44.2',
        'Aluminium unitised type 7: opaque panel, 100% aluminium cladding',
        'Double skin type 1: deep cavity aluminium façade, 100% single + DGU 66.2 + 8T-16-44.2',
        'Double skin type 2: narrow cavity aluminium façade, 100% single + DGU 66.2 + 8T-16-44.2',
    ],
    'Lifts': [
        'Category 5, 6 for high rise buildings (>10 floors)',
        'Category 3, 4 for medium rise buildings (4-10 floors)',
        'Category 1, 2 for low rise buildings (<4 floors)',
    ],
    'Escalator': ['Escalator'],
    'Mechanical, electrical, and plumbing (MEP)': [
        'Low or mid rise office, hotel or retail building (≤10 floors)',
        'Residential building',
        'Warehouse or industrial building',
        'High rise office, hotel or retail building (>10 floors)',
        'Hospital or high services intensity building',
    ],
    'Other': ['Other'],
    'Aluminium billet': ['-'],
};

const MATERIAL_TYPES = Object.keys(EF_MATCH_BY_MATERIAL_TYPE);

const LOCATIONS = [
  'Aus, ACT - Canberra',
  'Aus, NSW - Central Coast',
  'Aus, NSW - Newcastle',
  'Aus, NSW - Port Kembla (Steel)',
  'Aus, NSW - Sydney',
  'Aus, NSW - Wollongong',
  'Aus, NT - Darwin',
  'Aus, QLD - Brisbane',
  'Aus, QLD - Cairns',
  'Aus, QLD - Gold Coast',
  'Aus, QLD - Sunshine Coast',
  'Aus, QLD - Toowoomba',
  'Aus, QLD - Townsville',
  'Aus, SA - Adelaide',
  'Aus, SA - Whyalla (Steel)',
  'Aus, TAS - Hobart',
  'Aus, VIC - Ballarat',
  'Aus, VIC - Bendigo',
  'Aus, VIC - Geelong',
  'Aus, VIC - Melbourne',
  'Aus, WA - Perth',
  'Intl - Brazil',
  'Intl - Central America',
  'Intl - China',
  'Intl - East Africa',
  'Intl - European Union',
  'Intl - India',
  'Intl - Indonesia',
  'Intl - Ireland',
  'Intl - Japan',
  'Intl - Malaysia/Singapore',
  'Intl - Middle East',
  'Intl - New Zealand',
  'Intl - Russia',
  'Intl - South Africa',
  'Intl - South America, East Coast',
  'Intl - South America, West Coast',
  'Intl - South Korea',
  'Intl - Thailand',
  'Intl - Turkey',
  'Intl - United Kingdom',
  'Intl - USA, East Coast',
  'Intl - USA, West Coast',
  'Intl - West Africa',
];

// 'm' and '#' already exist in nabers_ref_ef data but were previously unselectable,
// so those records opened with a blank Unit box.
const UNITS = ['m²', 'm³', 'm', 'kg', 'tonne', '#'];
const DECLARED_UNITS = ['kg', 'm²', 'm³', 'm', 't', 'unit'];

// Starting column widths (px). The table uses a fixed layout so these are exact;
// anything not listed falls back to DEFAULT_COL_WIDTH. Users can drag any column
// and their sizes are remembered in localStorage.
const COL_WIDTHS_KEY = 'nabersEfColumnWidths';
const DEFAULT_COL_WIDTH = 130;
const DEFAULT_COL_WIDTHS = {
    select: 44,
    actions: 132,
    Id: 210,
    material_type: 150,
    name: 280,
    mur_status: 112,
    mur_id: 150,
    mur_name: 210,
    mur_category: 160,
    nezo_unit: 96,
    qu_status: 130,
    qu_name: 190,
    Data_valid_start: 112,
    Data_valid_end: 112,
    Ref_link: 100,
    ef_match: 220,
    location: 170,
    unit: 84,
    epd_registration_number: 150,
    epd_version: 100,
    material_classification: 170,
    declared_unit: 120,
    ExternalRefType: 140,
};
const MIN_COL_WIDTH = 56;

// quantity_types.Name → nezo CAD display unit
const NEZO_UNIT_BY_QT = {
    CubicMeters: 'm³',
    SquareMeters: 'm²',
    kg: 'kg',
    Meters: 'm',
    Count: '#',
    EA: '#',
};

// ✅ Date formatting helpers
const formatDate = (dateString) => {
    if (!dateString) return '-';

    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;

        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();

        return `${day}/${month}/${year}`;
    } catch (error) {
        return dateString;
    }
};

const formatDateForInput = (dateString) => {
    if (!dateString) return '';

    try {
        // If already in YYYY-MM-DD format, return as-is
        if (/^\d{4}-\d{2}-\d{2}/.test(dateString)) {
            return dateString.split('T')[0];
        }

        // If in DD/MM/YYYY format, convert to YYYY-MM-DD for input
        const parts = dateString.split('/');
        if (parts.length === 3) {
            const [day, month, year] = parts;
            return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }

        return dateString;
    } catch (error) {
        return '';
    }
};

// Format number for display
const formatNumber = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    return parseFloat(value).toFixed(7);
};

const EMPTY_FORM_DATA = {
    Id: '',
    material_type: '',
    name: '',
    Data_valid_start: '',
    Data_valid_end: '',
    Ref_link: '',
    ef_match: '',
    location: '',
    unit: '',
    density_kg_m3: '',
    area_density_kg_m2: '',
    mass_per_m_kg_m: '',
    mass_per_unit_kg_unit: '',
    gwp_total_kg_co2e_quant: '',
    gwp_fossil_kg_co2e_quant: '',
    gwp_biogenic_kg_co2e_quant: '',
    gwp_luluc_kg_co2e_quant: '',
    gwp_stored_kg_co2e_quant: '',
    ExternalRefType: 'NABERS',
    epd_registration_number: '',
    epd_version: '',
    material_classification: '',
    declared_unit: '',
    declared_gwp_total: '',
    declared_gwp_fossil: '',
    declared_gwp_biogenic: '',
    declared_gwp_luluc: '',
    declared_gwp_stored: '',
    density_is_estimated: 0,
    area_density_is_estimated: 0,
    mass_per_m_is_estimated: 0,
    mass_per_unit_is_estimated: 0,
    estimated_source_note: '',
};

const EMPTY_MUR_FORM = {
    exists: false,
    murId: '',
    physicalIds: [],
    name: '',
    categoryId: '',
    quantityTypeId: '',
    co2: '',
    biogenic: '',
    weight: '',
    externalRefType: 'NABERS',
    externalRefId: '',
};

const EMPTY_QU_FORM = {
    exists: false,
    quId: '',
    name: '',
    categoryId: '',
    quantityTypeId: '',
    colorHex: '#000000',
    breakdownCount: null,
};

// DB nulls -> '' so controlled inputs stay controlled
const nullToEmpty = (obj) => {
    const out = {};
    Object.keys(obj).forEach((k) => {
        out[k] = obj[k] === null || obj[k] === undefined ? '' : obj[k];
    });
    return out;
};

// Which flag column records that a physical property was supplied by hand
// (a looked-up typical value) rather than read off the EPD.
const ESTIMATED_FLAG_FOR = {
    density_kg_m3: 'density_is_estimated',
    area_density_kg_m2: 'area_density_is_estimated',
    mass_per_m_kg_m: 'mass_per_m_is_estimated',
    mass_per_unit_kg_unit: 'mass_per_unit_is_estimated',
};

const isEstimated = (item, field) => {
    const flag = ESTIMATED_FLAG_FOR[field];
    return !!(flag && item && (item[flag] === 1 || item[flag] === true || item[flag] === '1'));
};

const nezoUnitFor = (quantityTypeName) =>
    (quantityTypeName && NEZO_UNIT_BY_QT[quantityTypeName]) || quantityTypeName || null;

// /api/newmaterial/categories returns a FLAT array whose hierarchy lives only in
// ParentCategoryId, and whose id/name are lowercase (the route aliases them) while
// ParentCategoryId/Priority keep DB casing. Group it for a native <select>: a
// parent with children becomes an <optgroup>, so the browser handles the nesting
// instead of padded labels (which blow out the native popup width). Sort order
// mirrors CategoryPickerModal - Priority first, then name.
const buildCategoryGroups = (cats) => {
    const byId = new Map(cats.map((c) => [c.id, c]));
    const childrenOf = new Map();
    const roots = [];

    cats.forEach((c) => {
        const pid = c.ParentCategoryId && byId.has(c.ParentCategoryId) ? c.ParentCategoryId : null;
        if (pid) {
            if (!childrenOf.has(pid)) childrenOf.set(pid, []);
            childrenOf.get(pid).push(c);
        } else {
            roots.push(c);
        }
    });

    const sorted = (rows) => [...rows].sort((a, b) => {
        const ap = a.Priority == null ? Number.MAX_SAFE_INTEGER : a.Priority;
        const bp = b.Priority == null ? Number.MAX_SAFE_INTEGER : b.Priority;
        if (ap !== bp) return ap - bp;
        return String(a.name || '').localeCompare(String(b.name || ''));
    });

    const seen = new Set(); // guards against a malformed parent cycle
    // optgroup nests only one level, so grandchildren are flattened into the
    // group with a short marker rather than deep padding.
    const collect = (nodes, depth, acc) => {
        sorted(nodes).forEach((n) => {
            if (seen.has(n.id)) return;
            seen.add(n.id);
            const name = n.name || n.id;
            acc.push({ id: n.id, label: depth > 1 ? `\u00B7 ${name}` : name });
            collect(childrenOf.get(n.id) || [], depth + 1, acc);
        });
        return acc;
    };

    const out = [];
    sorted(roots).forEach((r) => {
        const kids = childrenOf.get(r.id) || [];
        if (kids.length) {
            seen.add(r.id); // container row: represented by the group header itself
            out.push({ type: 'group', id: r.id, label: r.name || r.id, options: collect(kids, 1, []) });
        } else if (!seen.has(r.id)) {
            seen.add(r.id);
            out.push({ type: 'option', id: r.id, label: r.name || r.id });
        }
    });

    return out;
};

// Every id the grouped list can actually select
const selectableIds = (nodes) =>
    nodes.flatMap((n) => (n.type === 'group' ? n.options.map((o) => o.id) : [n.id]));

const NabersRefEfPage = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const itemsPerPage = 50;
    const [showImportModal, setShowImportModal] = useState(false);
    const [showBulkModal, setShowBulkModal] = useState(false);
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [columnWidths, setColumnWidths] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem(COL_WIDTHS_KEY)) || {};
        } catch {
            return {};
        }
    });
    const [isFullPage, setIsFullPage] = useState(false);
    const [showDeclared, setShowDeclared] = useState(false);

    // Filter inputs vs. the applied set that actually drives fetching —
    // typing no longer refires the query on every keystroke.
    const emptyFilters = {
        id: '',
        material_type: '',
        name: '',
        Data_valid_start: '',
        Data_valid_end: '',
        location: '',
        ef_match: '',
        mur_id: '',
        mur_name: '',
        qu_id: '',
        qu_name: '',
        has_mur: '',
        has_qu: '',
        estimated: ''
    };
    const [filters, setFilters] = useState(emptyFilters);
    const [appliedFilters, setAppliedFilters] = useState(emptyFilters);

    // Record modal state
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState(EMPTY_FORM_DATA);
    const [murForm, setMurForm] = useState(EMPTY_MUR_FORM);
    const [quForm, setQuForm] = useState(EMPTY_QU_FORM);
    const [saving, setSaving] = useState(false);
    const [saveSummary, setSaveSummary] = useState(null); // { steps: [{label, status, detail}] }
    const [formSnapshot, setFormSnapshot] = useState('');
    // True only when a click STARTED on the overlay — stops a text selection
    // that ends outside the modal from closing it.
    const overlayMouseDown = useRef(false);

    // Dropdown sources for the MUR / CAD Material sections (same APIs the
    // New Co2/Weight and New Material pages use)
    const [epdCategories, setEpdCategories] = useState([]);
    const [quantityTypes, setQuantityTypes] = useState([]);
    const [materialCategories, setMaterialCategories] = useState([]);

    // ✅ Modal state for success/error notifications
    const [modal, setModal] = useState({
        open: false,
        title: '',
        message: '',
        icon: '✅',
        isError: false
    });

    // ✅ Helper functions for modals
    const showModal = (title, message, icon = '✅') => {
        setModal({ open: true, title, message, icon, isError: false });
    };

    const showErrorModal = (title, message) => {
        setModal({ open: true, title, message, icon: '❌', isError: true });
    };

    const closeModal = () => {
        setModal({ ...modal, open: false });
    };

    // Derived EF match options based on current selections
    const filterEfOptions = filters.material_type
        ? (EF_MATCH_BY_MATERIAL_TYPE[filters.material_type] || [])
        : [];

    const formEfOptions = formData.material_type
        ? (EF_MATCH_BY_MATERIAL_TYPE[formData.material_type] || [])
        : [];

    // Nezo EPD material types for the CAD Material section, grouped for the
    // native select. An already-linked QU filed outside this list (or on a
    // container row) still gets an option so the field isn't blank.
    const quCategoryGroups = buildCategoryGroups(
        materialCategories.filter(
            (c) => !c.IsHidden && (c.CategoryKind || 'BuildingElement') === 'MaterialType'
        )
    );
    const quCategoryFallback =
        quForm.categoryId && !selectableIds(quCategoryGroups).includes(quForm.categoryId)
            ? {
                id: quForm.categoryId,
                label: materialCategories.find((c) => c.id === quForm.categoryId)?.name || quForm.categoryId,
            }
            : null;

    // Remember column widths between visits
    useEffect(() => {
        try {
            localStorage.setItem(COL_WIDTHS_KEY, JSON.stringify(columnWidths));
        } catch { /* storage full or blocked — sizing just won't persist */ }
    }, [columnWidths]);

    // Full-page mode: declarative body class, cleaned up on unmount
    useEffect(() => {
        document.body.classList.toggle('nabers-ef-fullpage', isFullPage);
        return () => document.body.classList.remove('nabers-ef-fullpage');
    }, [isFullPage]);

    // Esc leaves full-page mode (ignored while a modal is open — Esc there is
    // for the modal, and closing both at once is disorienting)
    useEffect(() => {
        if (!isFullPage) return undefined;
        const onKeyDown = (e) => {
            if (e.key === 'Escape' && !showForm && !showImportModal) {
                setIsFullPage(false);
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [isFullPage, showForm, showImportModal]);

    // Load dropdown sources once (page is AU-gated; region header auto-injected)
    useEffect(() => {
        const load = async () => {
            try {
                const cats = await fetchCostCategories();
                const arr = Array.isArray(cats) ? cats : (Array.isArray(cats?.data) ? cats.data : []);
                setEpdCategories(arr.filter(c => c.Categories && c.Categories.trim().toUpperCase().startsWith('EPD')));
            } catch (e) {
                console.error('❌ Failed to load MUR categories:', e);
            }
            try {
                const qts = await fetchNewCostQuantityTypes();
                setQuantityTypes(Array.isArray(qts) ? qts : (Array.isArray(qts?.data) ? qts.data : []));
            } catch (e) {
                console.error('❌ Failed to load quantity types:', e);
            }
            try {
                const quCats = await fetchNewMaterialCategories();
                // Keep the full list — the MaterialType filter happens at render so a
                // legacy Product filed elsewhere can still resolve its category name.
                setMaterialCategories(Array.isArray(quCats) ? quCats : []);
            } catch (e) {
                console.error('❌ Failed to load material categories:', e);
            }
        };
        load();
    }, []);

    // Fetch data
    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = {
                page: currentPage,
                limit: itemsPerPage,
                ...appliedFilters
            };

            const response = await api.get('/nabers-ref-ef', { params });

            if (response.data.success) {
                setData(response.data.data);
                setTotalPages(response.data.pagination.totalPages);
                setTotalRecords(response.data.pagination.total);
            }
        } catch (err) {
            console.error('❌ Fetch error:', err.response || err);
            const errorMsg = 'Failed to fetch data: ' + (err.response?.data?.message || err.message);
            setError(errorMsg);
            showErrorModal('Fetch Error', errorMsg);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, appliedFilters]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Filter handlers
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => {
            const next = { ...prev, [name]: value };
            // Reset ef_match when material_type changes
            if (name === 'material_type') next.ef_match = '';
            return next;
        });
    };

    const handleFilterSubmit = (e) => {
        e.preventDefault();
        setCurrentPage(1);
        setAppliedFilters({ ...filters });
    };

    const handleClearFilters = () => {
        setFilters(emptyFilters);
        setAppliedFilters(emptyFilters);
        setCurrentPage(1);
    };

    // Pagination handlers
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    // Form handlers
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const next = { ...prev, [name]: value };
            // Reset ef_match when material_type changes
            if (name === 'material_type') next.ef_match = '';
            return next;
        });
    };

    const handleMurChange = (e) => {
        const { name, value } = e.target;
        setMurForm(prev => ({ ...prev, [name]: value }));
    };

    const handleQuChange = (e) => {
        const { name, value } = e.target;
        setQuForm(prev => ({ ...prev, [name]: value }));
    };

    // Snapshot of the three form states as the modal opened — used to tell
    // whether an Esc / outside-click would discard real work.
    const snapshotOf = (f, m, q) => JSON.stringify({ f, m, q });

    const handleAdd = () => {
        const f = EMPTY_FORM_DATA;
        const m = { ...EMPTY_MUR_FORM };
        const q = { ...EMPTY_QU_FORM };
        setFormData(f);
        setMurForm(m);
        setQuForm(q);
        setFormSnapshot(snapshotOf(f, m, q));
        setEditingId(null);
        setShowForm(true);
    };

    const handleEdit = async (id) => {
        try {
            const response = await api.get(`/nabers-ref-ef/${id}`);
            if (response.data.success) {
                const rec = response.data.data;
                const f = { ...EMPTY_FORM_DATA, ...nullToEmpty(rec) };

                const m = rec.mur_id
                    ? {
                        exists: true,
                        murId: rec.mur_id,
                        physicalIds: rec.mur_physical_ids || [],
                        name: rec.mur_name || '',
                        categoryId: rec.mur_category_id || '',
                        quantityTypeId: rec.mur_quantity_type_id != null ? String(rec.mur_quantity_type_id) : '',
                        co2: rec.mur_co2_value != null ? String(rec.mur_co2_value) : '',
                        biogenic: rec.mur_biogenic_value != null ? String(rec.mur_biogenic_value) : '',
                        weight: rec.mur_weight_value != null ? String(rec.mur_weight_value) : '',
                        externalRefType: 'NABERS',
                        externalRefId: rec.Id,
                    }
                    : {
                        ...EMPTY_MUR_FORM,
                        name: rec.name || '',
                        externalRefId: rec.Id,
                    };

                const q = rec.qu_id
                    ? {
                        exists: true,
                        quId: rec.qu_id,
                        name: rec.qu_name || '',
                        categoryId: rec.qu_category_id || '',
                        quantityTypeId: rec.qu_quantity_type_id != null ? String(rec.qu_quantity_type_id) : '',
                        colorHex: rec.qu_color_hex || '#000000',
                        breakdownCount: rec.qu_breakdown_count ?? 0,
                    }
                    : {
                        ...EMPTY_QU_FORM,
                        quantityTypeId: rec.mur_quantity_type_id != null ? String(rec.mur_quantity_type_id) : '',
                    };

                setFormData(f);
                setMurForm(m);
                setQuForm(q);
                setFormSnapshot(snapshotOf(f, m, q));
                setEditingId(id);
                setShowForm(true);
            }
        } catch (err) {
            console.error('❌ Edit fetch error:', err.response || err);
            showErrorModal('Fetch Error', 'Failed to fetch record: ' + (err.response?.data?.message || err.message));
        }
    };

    // ── One-Save orchestration ────────────────────────────────────────────────
    // Saves in order: EF record → MUR (create auto-linked, or update) → CAD
    // Material (create) → QU→MUR link. Each step reports its own outcome; a
    // failed step never rolls back earlier ones — re-saving completes the rest.
    const murCreateArmed = !murForm.exists && murForm.categoryId && murForm.quantityTypeId;
    const murPartiallyFilled = !murForm.exists && !murCreateArmed &&
        (murForm.categoryId || murForm.quantityTypeId || murForm.co2 || murForm.biogenic || murForm.weight);
    const quCreateArmed = !quForm.exists && quForm.name.trim() && quForm.categoryId && quForm.quantityTypeId;
    const quPartiallyFilled = !quForm.exists && !quCreateArmed &&
        (quForm.name.trim() || quForm.categoryId);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (saving) return;
        setSaving(true);

        const steps = [];
        const efId = editingId || formData.Id || uuidv4();

        // 1. EF record
        try {
            if (editingId) {
                await api.put(`/nabers-ref-ef/${editingId}`, formData);
            } else {
                await api.post('/nabers-ref-ef', { ...formData, Id: efId });
            }
            steps.push({ label: 'EPD record', status: 'ok', detail: editingId ? 'Updated' : 'Created' });
        } catch (err) {
            steps.push({
                label: 'EPD record',
                status: 'fail',
                detail: err.response?.data?.message || err.message
            });
            setSaving(false);
            setSaveSummary({ steps });
            return;
        }

        // 2. MUR
        let murPhysicalIds = murForm.physicalIds || [];
        if (murForm.exists) {
            try {
                await updateCo2Weight(murForm.murId, {
                    id: murForm.murId,
                    name: murForm.name,
                    co2Value: murForm.co2 === '' ? 0 : parseFloat(murForm.co2),
                    biogenicValue: murForm.biogenic === '' ? 0 : parseFloat(murForm.biogenic),
                    weightValue: murForm.weight === '' ? 0 : parseFloat(murForm.weight),
                    categoryId: murForm.categoryId,
                    quantityTypeId: String(murForm.quantityTypeId),
                    externalRefType: murForm.externalRefType || 'NABERS',
                    externalRefId: efId,
                });
                steps.push({ label: 'Measurement Unit Rate', status: 'ok', detail: 'Updated' });
            } catch (err) {
                steps.push({
                    label: 'Measurement Unit Rate',
                    status: 'fail',
                    detail: err.response?.data?.message || err.message
                });
            }
        } else if (murCreateArmed) {
            try {
                const murId = uuidv4();
                const resp = await addNewCo2Weight({
                    id: murId,
                    name: murForm.name || formData.name,
                    categoryId: murForm.categoryId,
                    quantityTypeId: String(murForm.quantityTypeId),
                    co2Value: parseFloat(murForm.co2) || 0,
                    biogenicValue: parseFloat(murForm.biogenic) || 0,
                    weightValue: parseFloat(murForm.weight) || 0,
                    externalRefType: murForm.externalRefType || 'NABERS',
                    externalRefId: efId,
                });
                const created = resp?.data?.data;
                if (created?.co2Id) {
                    murPhysicalIds = [created.co2Id, created.biogenicId, created.weightId];
                }
                steps.push({ label: 'Measurement Unit Rate', status: 'ok', detail: 'Created & linked to this EPD' });
            } catch (err) {
                steps.push({
                    label: 'Measurement Unit Rate',
                    status: 'fail',
                    detail: err.response?.data?.message || err.response?.data?.error || err.message
                });
            }
        } else if (murPartiallyFilled) {
            steps.push({
                label: 'Measurement Unit Rate',
                status: 'skip',
                detail: 'Not created — select both Category and Quantity Type to create a MUR'
            });
        }

        // 3. CAD Material (Product QU) + 4. link
        if (quCreateArmed) {
            let quId = null;
            try {
                quId = uuidv4();
                await addTestData({
                    Id: quId,
                    name: quForm.name.trim(),
                    description: '',
                    ColorHex: quForm.colorHex || '#000000',
                    quantityType: quForm.quantityTypeId,
                    categoryId: quForm.categoryId,
                    materialKind: 'Product',
                    visibility: 'public',
                });
                steps.push({ label: 'CAD Material (Product)', status: 'ok', detail: 'Created' });
            } catch (err) {
                quId = null;
                steps.push({
                    label: 'CAD Material (Product)',
                    status: 'fail',
                    detail: err.response?.data?.message || err.response?.data?.error || err.message
                });
            }

            if (quId) {
                if (murPhysicalIds.length) {
                    try {
                        const linkResp = await api.post(`/nabers-ref-ef/${efId}/link-material`, {
                            quantityUnitId: quId,
                            murIds: murPhysicalIds,
                        });
                        steps.push({
                            label: 'Material ↔ MUR link',
                            status: 'ok',
                            detail: `${linkResp.data.linked} rate line(s) linked`
                        });
                    } catch (err) {
                        steps.push({
                            label: 'Material ↔ MUR link',
                            status: 'fail',
                            detail: (err.response?.data?.message || err.message) + ' — link it in Material Editor'
                        });
                    }
                } else {
                    steps.push({
                        label: 'Material ↔ MUR link',
                        status: 'skip',
                        detail: 'No MUR available to link — add one and re-save, or link in Material Editor'
                    });
                }
            }
        } else if (quPartiallyFilled) {
            steps.push({
                label: 'CAD Material (Product)',
                status: 'skip',
                detail: 'Not created — Name, Material Type and Quantity Type are all required'
            });
        }

        setSaving(false);
        setShowForm(false);
        setEditingId(null);
        setSaveSummary({ steps });
        fetchData();
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this record?')) {
            return;
        }

        setLoading(true);
        try {
            await api.delete(`/nabers-ref-ef/${id}`);
            showModal('Success', 'Record deleted successfully', '✅');
            fetchData();
        } catch (err) {
            console.error('❌ Delete error:', err.response || err);
            showErrorModal('Delete Failed', 'Delete failed: ' + (err.response?.data?.message || err.message));
        } finally {
            setLoading(false);
        }
    };

    // Close the record modal, warning first if it would throw away edits.
    const requestCloseForm = () => {
        if (saving) return;
        const dirty = snapshotOf(formData, murForm, quForm) !== formSnapshot;
        if (dirty && !window.confirm('Discard your unsaved changes to this record?')) return;
        setShowForm(false);
        setEditingId(null);
    };

    const handleCancel = () => requestCloseForm();

    const handleOverlayMouseDown = (e) => {
        overlayMouseDown.current = e.target === e.currentTarget;
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget && overlayMouseDown.current) {
            requestCloseForm();
        }
        overlayMouseDown.current = false;
    };

    // Esc closes the record modal (same discard guard as the overlay click).
    // Declared after requestCloseForm so the dependency isn't in its TDZ.
    useEffect(() => {
        if (!showForm) return undefined;
        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                requestCloseForm();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showForm, saving, formData, murForm, quForm, formSnapshot]);

    // ── Table column model (single source for headers, cells and colSpan) ─────
    // Computed per render (≤50 rows) so cell handlers never close over stale state.
    // ── Column widths (drag to resize, double-click a handle to reset) ───────
    const widthFor = (key) =>
        columnWidths[key] ?? DEFAULT_COL_WIDTHS[key] ?? DEFAULT_COL_WIDTH;

    const startResize = (e, key) => {
        e.preventDefault();
        e.stopPropagation();
        const startX = e.clientX;
        const startWidth = widthFor(key);

        const onMove = (ev) => {
            const next = Math.max(MIN_COL_WIDTH, startWidth + (ev.clientX - startX));
            setColumnWidths((prev) => ({ ...prev, [key]: next }));
        };
        const onUp = () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
            document.body.classList.remove('col-resizing');
        };

        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
        document.body.classList.add('col-resizing');
    };

    // The first three columns stay pinned while scrolling right, so a row's
    // identity is never lost. They must be the leading columns for the running
    // offset below to be correct.
    const STICKY_KEYS = ['select', 'actions', 'Id'];

    const stickyLeftFor = (cols, key) => {
        if (!STICKY_KEYS.includes(key)) return null;
        let offset = 0;
        for (const c of cols) {
            if (c.key === key) return offset;
            if (STICKY_KEYS.includes(c.key)) offset += widthFor(c.key);
        }
        return null;
    };

    const resetWidth = (key) => {
        setColumnWidths((prev) => {
            const next = { ...prev };
            delete next[key];
            return next;
        });
    };

    // Row selection for bulk generation. Must be declared BEFORE the columns
    // IIFE below, which runs immediately and reads these values.
    const toggleRow = (id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const pageIds = data.map((r) => r.Id);
    const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));

    const toggleAllOnPage = () => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (allPageSelected) pageIds.forEach((id) => next.delete(id));
            else pageIds.forEach((id) => next.add(id));
            return next;
        });
    };

    // Only rows currently loaded can be previewed; selection persists across pages
    // but the modal's "selected" scope uses what it can actually see.
    const selectedRows = data.filter((r) => selectedIds.has(r.Id));

    const columns = (() => {
        // A property supplied by hand is shown red so an estimate is never mistaken
        // for a figure taken off the EPD; the source note is the tooltip.
        const estimatedNumber = (item, field) => {
            const text = formatNumber(item[field]);
            if (!isEstimated(item, field)) return text;
            return (
                <span
                    className="estimated-value"
                    title={`Manually supplied estimate${item.estimated_source_note ? ' — ' + item.estimated_source_note : ''}`}
                >
                    {text} ⚠
                </span>
            );
        };

        // Full text — the column is resizable and cells wrap, so nothing is cut off.
        // The title still gives a hover readout when a column is dragged narrow.
        const cellText = (value) => {
            if (value === null || value === undefined || value === '') return '-';
            const s = String(value);
            return <span className="wrap-cell" title={s}>{s}</span>;
        };

        const murBadge = (item) => {
            if (item.mur_count > 1) return <span className="link-badge badge-multi" title="Multiple MURs reference this EF id">Multiple</span>;
            if (item.mur_id) return <span className="link-badge badge-ok">MUR ✓</span>;
            return (
                <span className="badge-with-action">
                    <span className="link-badge badge-missing">No MUR</span>
                    <button type="button" className="btn-add-inline" title="Open the record to add a MUR"
                        onClick={() => handleEdit(item.Id)} disabled={loading}>＋</button>
                </span>
            );
        };

        const quBadge = (item) => {
            if (!item.mur_id) return <span className="link-badge badge-none">—</span>;
            if (item.qu_id) return <span className="link-badge badge-ok">QU ✓</span>;
            return (
                <span className="badge-with-action">
                    <span className="link-badge badge-warn">MUR, no QU</span>
                    <button type="button" className="btn-add-inline" title="Open the record to add a CAD material"
                        onClick={() => handleEdit(item.Id)} disabled={loading}>＋</button>
                </span>
            );
        };

        const base = [
            {
                key: 'select',
                label: (
                    <input
                        type="checkbox"
                        checked={allPageSelected}
                        onChange={toggleAllOnPage}
                        title="Select all rows on this page"
                    />
                ),
                render: (item) => (
                    <input
                        type="checkbox"
                        checked={selectedIds.has(item.Id)}
                        onChange={() => toggleRow(item.Id)}
                    />
                ),
            },
            { key: 'actions', label: 'Actions', render: (item) => (
                <div className="action-buttons">
                    <button className="btn-edit-small" onClick={() => handleEdit(item.Id)} disabled={loading}>Edit</button>
                    <button className="btn-delete-small" onClick={() => handleDelete(item.Id)} disabled={loading}>Delete</button>
                </div>
            ) },
            { key: 'Id', label: 'ID', render: (item) => <code style={{fontSize: '0.85em', color: '#666'}}>{item.Id}</code> },
            { key: 'material_type', label: 'Material Type', render: (item) => item.material_type || '-' },
            { key: 'name', label: 'Name', render: (item) => item.name || '-' },
            // ── Nezo linkage group ──
            { key: 'mur_status', label: 'MUR', render: murBadge },
            { key: 'mur_id', label: 'MUR ID', render: (item) => item.mur_id
                ? <code className="wrap-cell" style={{fontSize: '0.8em', color: '#666'}} title={item.mur_id}>{item.mur_id}</code>
                : '-' },
            { key: 'mur_name', label: 'MUR Name', render: (item) => cellText(item.mur_name) },
            { key: 'mur_category', label: 'MUR Category', render: (item) => cellText(item.mur_category) },
            { key: 'nezo_unit', label: 'Nezo Unit', render: (item) => item.mur_id ? (nezoUnitFor(item.mur_quantity_type) || '-') : '-' },
            { key: 'qu_status', label: 'Product QU', render: quBadge },
            { key: 'qu_name', label: 'QU Name', render: (item) => cellText(item.qu_name) },
            // ── Original columns ──
            { key: 'Data_valid_start', label: 'Valid Start', render: (item) => formatDate(item.Data_valid_start) },
            { key: 'Data_valid_end', label: 'Valid End', render: (item) => formatDate(item.Data_valid_end) },
            { key: 'Ref_link', label: 'Reference Link', render: (item) => item.Ref_link ? (
                <a href={item.Ref_link} target="_blank" rel="noopener noreferrer" style={{color: '#2e8a51ff', textDecoration: 'underline'}}>Link</a>
            ) : '-' },
            { key: 'ef_match', label: 'EF Match', render: (item) => item.ef_match || '-' },
            { key: 'location', label: 'Location', render: (item) => item.location || '-' },
            { key: 'unit', label: 'Unit', render: (item) => item.unit || '-' },
            // ── EPD metadata ──
            { key: 'epd_registration_number', label: <span>EPD Reg. No.</span>, render: (item) => cellText(item.epd_registration_number) },
            { key: 'epd_version', label: 'Version', render: (item) => item.epd_version || '-' },
            { key: 'material_classification', label: 'Classification', render: (item) => cellText(item.material_classification) },
            { key: 'declared_unit', label: 'Declared Unit', render: (item) => item.declared_unit || '-' },
            // ── Physical + nezo GWP ──
            { key: 'density_kg_m3', label: <span>Density<br/>(kg/m³)</span>, render: (item) => estimatedNumber(item, 'density_kg_m3') },
            { key: 'area_density_kg_m2', label: <span>Area Density<br/>(kg/m²)</span>, render: (item) => estimatedNumber(item, 'area_density_kg_m2') },
            { key: 'mass_per_m_kg_m', label: <span>Mass/m<br/>(kg/m)</span>, render: (item) => estimatedNumber(item, 'mass_per_m_kg_m') },
            { key: 'mass_per_unit_kg_unit', label: <span>Mass/Unit<br/>(kg/unit)</span>, render: (item) => estimatedNumber(item, 'mass_per_unit_kg_unit') },
            { key: 'gwp_total_kg_co2e_quant', label: <span>GWP Total<br/>(kg CO₂e/quant)</span>, render: (item) => formatNumber(item.gwp_total_kg_co2e_quant) },
            { key: 'gwp_fossil_kg_co2e_quant', label: <span>GWP Fossil<br/>(kg CO₂e/quant)</span>, render: (item) => formatNumber(item.gwp_fossil_kg_co2e_quant) },
            { key: 'gwp_biogenic_kg_co2e_quant', label: <span>GWP Biogenic<br/>(kg CO₂e/quant)</span>, render: (item) => formatNumber(item.gwp_biogenic_kg_co2e_quant) },
            { key: 'gwp_luluc_kg_co2e_quant', label: <span>GWP LULUC<br/>(kg CO₂e/quant)</span>, render: (item) => formatNumber(item.gwp_luluc_kg_co2e_quant) },
            { key: 'gwp_stored_kg_co2e_quant', label: <span>GWP Stored<br/>(kg CO₂e/quant)</span>, render: (item) => formatNumber(item.gwp_stored_kg_co2e_quant) },
        ];

        const declared = [
            { key: 'declared_gwp_total', label: <span>GWP Total<br/>(declared unit)</span>, render: (item) => formatNumber(item.declared_gwp_total) },
            { key: 'declared_gwp_fossil', label: <span>GWP Fossil<br/>(declared unit)</span>, render: (item) => formatNumber(item.declared_gwp_fossil) },
            { key: 'declared_gwp_biogenic', label: <span>GWP Biogenic<br/>(declared unit)</span>, render: (item) => formatNumber(item.declared_gwp_biogenic) },
            { key: 'declared_gwp_luluc', label: <span>GWP LULUC<br/>(declared unit)</span>, render: (item) => formatNumber(item.declared_gwp_luluc) },
            { key: 'declared_gwp_stored', label: <span>GWP Stored<br/>(declared unit)</span>, render: (item) => formatNumber(item.declared_gwp_stored) },
        ];

        const tail = [
            { key: 'ExternalRefType', label: 'External Ref Type', render: (item) => item.ExternalRefType || '-' },
        ];

        return showDeclared ? [...base, ...declared, ...tail] : [...base, ...tail];
    })();

    // ── Row selection for bulk generation ────────────────────────────────────
    const rowClass = (item) => {
        if (!item.mur_id) return 'row-missing-mur';
        if (!item.qu_id) return 'row-missing-qu';
        return '';
    };

    const stepIcon = { ok: '✅', fail: '❌', skip: '⏭️' };

    return (
        <div className={`nabers-ref-ef-page${isFullPage ? ' full-page' : ''}`}>
            <div className="page-header">
                <h1>NABERS Reference Emission Factors (Australia)</h1>
                <button
                    className="btn-fullpage"
                    onClick={() => setIsFullPage(v => !v)}
                    title={isFullPage ? 'Exit full page' : 'Expand to full page'}
                >
                    {isFullPage ? '🗗 Exit Full Page' : '⛶ Full Page'}
                </button>
                 <button
                    className="btn-import"
                    onClick={() => setShowImportModal(true)}
                    disabled={loading}
                >
                    📊 Import Data
                </button>
                <button
                    className="btn-add"
                    onClick={handleAdd}
                    disabled={loading}
                >
                    + Add New Record
                </button>
            </div>

            {error && <div className="error-message">{error}</div>}

            {/* Two-column body: filters on the left, results alongside */}
            <div className="nabers-body">

            {/* Filter Section */}
            <div className="filter-section">
                <h3>Filters</h3>
                <form onSubmit={handleFilterSubmit} className="filter-form">
                    <div className="filter-row">
                        <input
                            type="text"
                            name="id"
                            placeholder="ID"
                            value={filters.id}
                            onChange={handleFilterChange}
                        />

                        {/* Material Type filter — dropdown */}
                        <select
                            name="material_type"
                            value={filters.material_type}
                            onChange={handleFilterChange}
                        >
                            <option value="">All Material Types</option>
                            {MATERIAL_TYPES.map(mt => (
                                <option key={mt} value={mt}>{mt}</option>
                            ))}
                        </select>

                        <input
                            type="text"
                            name="name"
                            placeholder="Name"
                            value={filters.name}
                            onChange={handleFilterChange}
                        />
                        <input
                            type="date"
                            name="Data_valid_start"
                            placeholder="Valid Start Date"
                            value={filters.Data_valid_start}
                            onChange={handleFilterChange}
                        />
                        <input
                            type="date"
                            name="Data_valid_end"
                            placeholder="Valid End Date"
                            value={filters.Data_valid_end}
                            onChange={handleFilterChange}
                        />

                        {/* EF Match filter — dependent dropdown */}
                        <select
                            name="ef_match"
                            value={filters.ef_match}
                            onChange={handleFilterChange}
                            disabled={!filters.material_type}
                        >
                            <option value="">
                                {filters.material_type ? 'All EF Matches' : 'Select material type first'}
                            </option>
                            {filterEfOptions.map(ef => (
                                <option key={ef} value={ef}>{ef}</option>
                            ))}
                        </select>

                        <input
                            type="text"
                            name="location"
                            placeholder="Location"
                            value={filters.location}
                            onChange={handleFilterChange}
                        />

                        {/* Linkage filters — applied server-side in the count + page queries */}
                        <div className="filter-divider">Linkage</div>

                        <select name="has_mur" value={filters.has_mur} onChange={handleFilterChange}>
                            <option value="">MUR: any</option>
                            <option value="yes">Has MUR</option>
                            <option value="no">No MUR</option>
                        </select>
                        <input
                            type="text"
                            name="mur_id"
                            placeholder="MUR ID"
                            value={filters.mur_id}
                            onChange={handleFilterChange}
                        />
                        <input
                            type="text"
                            name="mur_name"
                            placeholder="MUR Name"
                            value={filters.mur_name}
                            onChange={handleFilterChange}
                        />

                        <select name="has_qu" value={filters.has_qu} onChange={handleFilterChange}>
                            <option value="">Product QU: any</option>
                            <option value="yes">Has QU</option>
                            <option value="no">No QU</option>
                        </select>
                        <input
                            type="text"
                            name="qu_id"
                            placeholder="QU ID"
                            value={filters.qu_id}
                            onChange={handleFilterChange}
                        />
                        <input
                            type="text"
                            name="qu_name"
                            placeholder="QU Name"
                            value={filters.qu_name}
                            onChange={handleFilterChange}
                        />

                        <div className="filter-divider">Properties</div>
                        <select name="estimated" value={filters.estimated} onChange={handleFilterChange}>
                            <option value="">Properties: any</option>
                            <option value="yes">Has estimated values</option>
                            <option value="no">EPD values only</option>
                        </select>
                    </div>
                    <div className="filter-buttons">
                        <button type="submit" className="btn-filter">Apply Filters</button>
                        <button type="button" className="btn-clear" onClick={handleClearFilters}>
                            Clear Filters
                        </button>
                        <label className="declared-toggle">
                            <input
                                type="checkbox"
                                checked={showDeclared}
                                onChange={(e) => setShowDeclared(e.target.checked)}
                            />
                            Show declared values
                        </label>
                    </div>
                </form>
            </div>

            {/* Results column */}
            <div className="nabers-results">

            {/* Bulk actions strip */}
            <div className="bulk-toolbar">
                <span className="bulk-toolbar-count">
                    {selectedIds.size > 0
                        ? `${selectedIds.size} row${selectedIds.size === 1 ? '' : 's'} selected`
                        : 'No rows selected'}
                </span>
                {selectedIds.size > 0 && (
                    <button type="button" className="btn-clear" onClick={() => setSelectedIds(new Set())}>
                        Clear selection
                    </button>
                )}
                <button
                    type="button"
                    className="btn-bulk"
                    onClick={() => setShowBulkModal(true)}
                    disabled={loading}
                    title="Create the missing MURs and Product materials for these EPD records"
                >
                    ⚡ Generate missing links
                </button>
            </div>

            {showBulkModal && (
                <BulkGenerateModal
                    selectedRows={selectedRows}
                    appliedFilters={appliedFilters}
                    epdCategories={epdCategories}
                    materialTypeGroups={quCategoryGroups}
                    onClose={() => setShowBulkModal(false)}
                    onComplete={() => {
                        setShowBulkModal(false);
                        setSelectedIds(new Set());
                        fetchData();
                    }}
                />
            )}

            {/* Import Modal */}
            {showImportModal && (
                <NabersImportModal
                    epdCategories={epdCategories}
                    materialTypeGroups={quCategoryGroups}
                    onClose={() => setShowImportModal(false)}
                    onImportComplete={() => {
                        setShowImportModal(false);
                        fetchData();
                    }}
                />
            )}

            {/* Record Modal */}
            {showForm && (
                <div
                    className="modal-overlay"
                    onMouseDown={handleOverlayMouseDown}
                    onClick={handleOverlayClick}
                >
                    <div className="modal-content record-modal" onClick={(e) => e.stopPropagation()}>
                        <button
                            type="button"
                            className="modal-close-x"
                            onClick={requestCloseForm}
                            title="Close (Esc)"
                            aria-label="Close"
                        >
                            ×
                        </button>
                        <h2>{editingId ? 'Record' : 'New Record'}</h2>
                        <form onSubmit={handleSubmit} className="nabers-form">
                            <div className="form-grid record-grid">
                                {/* 1. Basic Information */}
                                <div className="form-section">
                                    <h4>Basic Information</h4>
                                    <div className="form-group">
                                        <label className="form-label">ID</label>
                                        <input
                                            type="text"
                                            className="form-input id-display"
                                            value={formData.Id || ''}
                                            onChange={(e) => setFormData({ ...formData, Id: e.target.value })}
                                            disabled={editingId !== null}
                                            placeholder={editingId ? '' : 'Enter unique ID (optional - auto-generates if empty)'}
                                        />
                                        </div>

                                    {/* Material Type — dropdown */}
                                    <div className="form-group">
                                        <label>Material Type:</label>
                                        <select
                                            name="material_type"
                                            value={formData.material_type}
                                            onChange={handleInputChange}
                                        >
                                            <option value="">— Select material type —</option>
                                            {MATERIAL_TYPES.map(mt => (
                                                <option key={mt} value={mt}>{mt}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label>Name:</label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            maxLength="255"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Valid Start Date:</label>
                                        <input
                                            type="date"
                                            name="Data_valid_start"
                                            value={formatDateForInput(formData.Data_valid_start)}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Valid End Date:</label>
                                        <input
                                            type="date"
                                            name="Data_valid_end"
                                            value={formatDateForInput(formData.Data_valid_end)}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Reference Link:</label>
                                        <input
                                            type="url"
                                            name="Ref_link"
                                            value={formData.Ref_link}
                                            onChange={handleInputChange}
                                            maxLength="500"
                                            placeholder="https://"
                                        />
                                    </div>

                                    {/* EF Match — dependent dropdown */}
                                    <div className="form-group">
                                        <label>EF Match:</label>
                                        <select
                                            name="ef_match"
                                            value={formData.ef_match}
                                            onChange={handleInputChange}
                                            disabled={!formData.material_type}
                                        >
                                            <option value="">
                                                {formData.material_type
                                                    ? '— Select EF match —'
                                                    : 'Select material type first'}
                                            </option>
                                            {formEfOptions.map(ef => (
                                                <option key={ef} value={ef}>{ef}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label>Location:</label>
                                        <select
                                            name="location"
                                            value={formData.location}
                                            onChange={handleInputChange}
                                            >
                                            <option value="">— Select location —</option>
                                            {LOCATIONS.map(location => (
                                                <option key={location} value={location}>{location}</option>
                                            ))}
                                            </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Unit:</label>
                                        <select
                                            name="unit"
                                            value={formData.unit}
                                            onChange={handleInputChange}
                                            >
                                            <option value="">— Select unit —</option>
                                            {UNITS.map(unit => (
                                                <option key={unit} value={unit}>{unit}</option>
                                            ))}
                                            </select>
                                    </div>
                                    <div className="form-group">
                                        <label>External Ref Type:</label>
                                        <input
                                            type="text"
                                            name="ExternalRefType"
                                            value={formData.ExternalRefType}
                                            onChange={handleInputChange}
                                            maxLength="45"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Registration Number:</label>
                                        <input
                                            type="text"
                                            name="epd_registration_number"
                                            value={formData.epd_registration_number}
                                            onChange={handleInputChange}
                                            maxLength="100"
                                            placeholder="e.g. S-P-01234"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Version:</label>
                                        <input
                                            type="text"
                                            name="epd_version"
                                            value={formData.epd_version}
                                            onChange={handleInputChange}
                                            maxLength="45"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Material Classification:</label>
                                        <input
                                            type="text"
                                            name="material_classification"
                                            value={formData.material_classification}
                                            onChange={handleInputChange}
                                            maxLength="255"
                                        />
                                    </div>
                                </div>

                                {/* 2. Physical Properties */}
                                <div className="form-section">
                                    <h4>Physical Properties</h4>
                                    {[
                                        { field: 'density_kg_m3', label: 'Density (kg/m³)' },
                                        { field: 'area_density_kg_m2', label: 'Area Density (kg/m²)' },
                                        { field: 'mass_per_m_kg_m', label: 'Mass per m (kg/m)' },
                                        { field: 'mass_per_unit_kg_unit', label: 'Mass per Unit (kg/unit)' },
                                    ].map(({ field, label }) => {
                                        const flag = ESTIMATED_FLAG_FOR[field];
                                        const est = isEstimated(formData, field);
                                        return (
                                            <div className="form-group" key={field}>
                                                <label className={est ? 'estimated-label' : ''}>
                                                    {label}:
                                                    {est && <span className="estimated-badge">estimated</span>}
                                                </label>
                                                <input
                                                    type="number"
                                                    step="0.0000001"
                                                    name={field}
                                                    className={est ? 'estimated-input' : ''}
                                                    value={formData[field]}
                                                    onChange={handleInputChange}
                                                />
                                                {est && (
                                                    <button
                                                        type="button"
                                                        className="estimated-clear"
                                                        onClick={() => setFormData((p) => ({ ...p, [flag]: 0 }))}
                                                    >
                                                        This is now an EPD value — clear the estimate flag
                                                    </button>
                                                )}
                                            </div>
                                        );
                                    })}
                                    <div className="form-group">
                                        <label>Estimate source note:</label>
                                        <input
                                            type="text"
                                            name="estimated_source_note"
                                            value={formData.estimated_source_note}
                                            onChange={handleInputChange}
                                            maxLength="255"
                                            placeholder="Where a manually supplied figure came from"
                                        />
                                    </div>
                                </div>

                                {/* 3. GWP Values — nezo converted */}
                                <div className="form-section">
                                    <h4>GWP Values (kg CO₂e/quant)</h4>
                                    <div className="form-group">
                                        <label>GWP Total:</label>
                                        <input
                                            type="number"
                                            step="0.0000001"
                                            name="gwp_total_kg_co2e_quant"
                                            value={formData.gwp_total_kg_co2e_quant}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>GWP Fossil:</label>
                                        <input
                                            type="number"
                                            step="0.0000001"
                                            name="gwp_fossil_kg_co2e_quant"
                                            value={formData.gwp_fossil_kg_co2e_quant}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>GWP Biogenic:</label>
                                        <input
                                            type="number"
                                            step="0.0000001"
                                            name="gwp_biogenic_kg_co2e_quant"
                                            value={formData.gwp_biogenic_kg_co2e_quant}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>GWP LULUC:</label>
                                        <input
                                            type="number"
                                            step="0.0000001"
                                            name="gwp_luluc_kg_co2e_quant"
                                            value={formData.gwp_luluc_kg_co2e_quant}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>GWP Stored:</label>
                                        <input
                                            type="number"
                                            step="0.0000001"
                                            name="gwp_stored_kg_co2e_quant"
                                            value={formData.gwp_stored_kg_co2e_quant}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                </div>

                                {/* 4. GWP Values — as declared on the EPD */}
                                <div className="form-section">
                                    <h4>GWP Values (kg CO₂e/<span className="unit-accent">Unit</span>)</h4>
                                    <div className="form-group">
                                        <label>GWP Total:</label>
                                        <input
                                            type="number"
                                            step="0.0000001"
                                            name="declared_gwp_total"
                                            value={formData.declared_gwp_total}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>GWP Fossil:</label>
                                        <input
                                            type="number"
                                            step="0.0000001"
                                            name="declared_gwp_fossil"
                                            value={formData.declared_gwp_fossil}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>GWP Biogenic:</label>
                                        <input
                                            type="number"
                                            step="0.0000001"
                                            name="declared_gwp_biogenic"
                                            value={formData.declared_gwp_biogenic}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>GWP LULUC:</label>
                                        <input
                                            type="number"
                                            step="0.0000001"
                                            name="declared_gwp_luluc"
                                            value={formData.declared_gwp_luluc}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>GWP Stored:</label>
                                        <input
                                            type="number"
                                            step="0.0000001"
                                            name="declared_gwp_stored"
                                            value={formData.declared_gwp_stored}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="unit-accent-label">Declared Unit:</label>
                                        <input
                                            type="text"
                                            name="declared_unit"
                                            list="declared-unit-options"
                                            value={formData.declared_unit}
                                            onChange={handleInputChange}
                                            maxLength="45"
                                            placeholder="e.g. kg"
                                        />
                                        <datalist id="declared-unit-options">
                                            {DECLARED_UNITS.map(u => <option key={u} value={u} />)}
                                        </datalist>
                                    </div>
                                </div>

                                {/* 5. Measurement Unit Rate */}
                                <div className="form-section linked-section">
                                    <h4>Measurement Unit Rate</h4>
                                    {murForm.exists ? (
                                        <div className="linked-hint linked-hint-ok">Linked MUR — edits save to the Co2/Weight record.</div>
                                    ) : (
                                        <div className="linked-hint">No MUR linked. Pick a Category and Quantity Type to create one on save (auto-linked via External Ref ID).</div>
                                    )}
                                    <div className="form-group">
                                        <label>ID:</label>
                                        <input type="text" value={murForm.murId} disabled
                                            placeholder="Auto-generated on save" />
                                    </div>
                                    <div className="form-group">
                                        <label>Name:</label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={murForm.name}
                                            onChange={handleMurChange}
                                            maxLength="255"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Category:</label>
                                        <select name="categoryId" value={murForm.categoryId} onChange={handleMurChange}>
                                            <option value="">— Select category —</option>
                                            {epdCategories.map(cat => (
                                                <option key={cat.Id} value={cat.Id}>{cat.Categories}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Quantity Type:</label>
                                        <select name="quantityTypeId" value={murForm.quantityTypeId} onChange={handleMurChange}>
                                            <option value="">— Select quantity type —</option>
                                            {quantityTypes.map(qt => (
                                                <option key={qt.Id} value={qt.Id}>{qt.Name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Co2:</label>
                                        <input type="number" step="0.00001" name="co2"
                                            value={murForm.co2} onChange={handleMurChange} />
                                    </div>
                                    <div className="form-group">
                                        <label>Biogenic:</label>
                                        <input type="number" step="0.00001" name="biogenic"
                                            value={murForm.biogenic} onChange={handleMurChange} />
                                    </div>
                                    <div className="form-group">
                                        <label>Weight:</label>
                                        <input type="number" step="0.00001" name="weight"
                                            value={murForm.weight} onChange={handleMurChange} />
                                    </div>
                                    <div className="form-group">
                                        <label>External Ref Type:</label>
                                        <input type="text" name="externalRefType" maxLength="45"
                                            value={murForm.externalRefType} onChange={handleMurChange} />
                                    </div>
                                    <div className="form-group">
                                        <label>External Ref ID:</label>
                                        <input type="text" value={murForm.externalRefId || formData.Id} disabled
                                            title="Set automatically to this EPD record's ID"
                                            placeholder="This record's ID (auto)" />
                                    </div>
                                </div>

                                {/* 6. Optional — CAD Material (Product QU) */}
                                <div className="form-section linked-section optional-section">
                                    <h4><span className="optional-tag">Optional</span> CAD Material — Product</h4>
                                    {quForm.exists ? (
                                        <div className="linked-hint linked-hint-ok">Linked Product material.</div>
                                    ) : (
                                        <div className="linked-hint">Fill Name, Material Type and Quantity Type to create a Product material on save, linked to the MUR.</div>
                                    )}
                                    <div className="form-group">
                                        <label>ID:</label>
                                        <input type="text" value={quForm.quId} disabled
                                            placeholder="Auto-generated on save" />
                                    </div>
                                    <div className="form-group">
                                        <label>Name:</label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={quForm.name}
                                            onChange={handleQuChange}
                                            maxLength="255"
                                            disabled={quForm.exists}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Material Type (Nezo EPD):</label>
                                        <select name="categoryId" value={quForm.categoryId} onChange={handleQuChange} disabled={quForm.exists}>
                                            <option value="">— Select material type —</option>
                                            {quCategoryGroups.map(node => (
                                                node.type === 'group' ? (
                                                    <optgroup key={node.id} label={node.label}>
                                                        {node.options.map(opt => (
                                                            <option key={opt.id} value={opt.id}>{opt.label}</option>
                                                        ))}
                                                    </optgroup>
                                                ) : (
                                                    <option key={node.id} value={node.id}>{node.label}</option>
                                                )
                                            ))}
                                            {quCategoryFallback && (
                                                <option value={quCategoryFallback.id}>{quCategoryFallback.label}</option>
                                            )}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Quantity Type:</label>
                                        <select name="quantityTypeId" value={quForm.quantityTypeId} onChange={handleQuChange} disabled={quForm.exists}>
                                            <option value="">— Select quantity type —</option>
                                            {quantityTypes.map(qt => (
                                                <option key={qt.Id} value={qt.Id}>{qt.Name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Colour:</label>
                                        <input type="color" name="colorHex"
                                            value={quForm.colorHex || '#000000'} onChange={handleQuChange} disabled={quForm.exists} />
                                    </div>
                                    {quForm.exists && (
                                        <div className={`breakdown-note ${quForm.breakdownCount > 0 ? 'breakdown-ok' : 'breakdown-missing'}`}>
                                            {quForm.breakdownCount > 0
                                                ? `Breakdown: ✓ attached (${quForm.breakdownCount} line${quForm.breakdownCount === 1 ? '' : 's'})`
                                                : 'Breakdown: ⚠ none attached — add via the New Breakdown page'}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="form-actions">
                                <button type="submit" className="btn-save" disabled={saving}>
                                    {saving ? 'Saving…' : 'Save'}
                                </button>
                                <button type="button" className="btn-cancel" onClick={handleCancel} disabled={saving}>
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Save summary */}
            {saveSummary && (
                <div className="notification-modal-overlay" onClick={() => setSaveSummary(null)}>
                    <div className="notification-modal-content save-summary" onClick={(e) => e.stopPropagation()}>
                        <h2 className="notification-title">Save Results</h2>
                        <ul className="save-summary-list">
                            {saveSummary.steps.map((step, i) => (
                                <li key={i} className={`save-step save-step-${step.status}`}>
                                    <span className="save-step-icon">{stepIcon[step.status]}</span>
                                    <span className="save-step-label">{step.label}:</span>
                                    <span className="save-step-detail">{step.detail}</span>
                                </li>
                            ))}
                        </ul>
                        <button className="notification-btn success-btn" onClick={() => setSaveSummary(null)}>OK</button>
                    </div>
                </div>
            )}

            {/* Data Table */}
            <div className="table-container">
                <table
                    className="nabers-table nabers-table-resizable"
                    style={{ width: columns.reduce((sum, c) => sum + widthFor(c.key), 0) }}
                >
                    <colgroup>
                        {columns.map(col => (
                            <col key={col.key} style={{ width: widthFor(col.key) }} />
                        ))}
                    </colgroup>
                    <thead>
                        <tr>
                            {columns.map((col, i) => {
                                const left = stickyLeftFor(columns, col.key);
                                const isLastSticky = STICKY_KEYS.includes(col.key)
                                    && !STICKY_KEYS.includes(columns[i + 1]?.key);
                                return (
                                    <th
                                        key={col.key}
                                        className={`${left !== null ? 'sticky-col' : ''} ${isLastSticky ? 'sticky-col-last' : ''}`}
                                        style={left !== null ? { left } : undefined}
                                    >
                                        <div className="th-inner">{col.label}</div>
                                        <span
                                            className="col-resizer"
                                            onMouseDown={(e) => startResize(e, col.key)}
                                            onDoubleClick={() => resetWidth(col.key)}
                                            title="Drag to resize · double-click to reset"
                                        />
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {loading && data.length === 0 ? (
                            <tr>
                                <td colSpan={columns.length} style={{ textAlign: 'center' }}>Loading...</td>
                            </tr>
                        ) : data.length === 0 ? (
                            <tr>
                                <td colSpan={columns.length} style={{ textAlign: 'center' }}>No records found</td>
                            </tr>
                        ) : (
                            data.map((item) => (
                                <tr key={item.Id} className={rowClass(item)}>
                                    {columns.map((col, i) => {
                                        const left = stickyLeftFor(columns, col.key);
                                        const isLastSticky = STICKY_KEYS.includes(col.key)
                                            && !STICKY_KEYS.includes(columns[i + 1]?.key);
                                        return (
                                            <td
                                                key={col.key}
                                                className={`${left !== null ? 'sticky-col' : ''} ${isLastSticky ? 'sticky-col-last' : ''}`}
                                                style={left !== null ? { left } : undefined}
                                            >
                                                {col.render(item)}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            <div className="pagination-container">
                <div className="pagination-info">
                    Showing {data.length > 0 ? ((currentPage - 1) * itemsPerPage + 1) : 0} to{' '}
                    {Math.min(currentPage * itemsPerPage, totalRecords)} of {totalRecords} records
                </div>
                <div className="pagination-controls">
                    <button
                        onClick={() => handlePageChange(1)}
                        disabled={currentPage === 1 || loading}
                        className="pagination-btn"
                    >
                        First
                    </button>
                    <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1 || loading}
                        className="pagination-btn"
                    >
                        Previous
                    </button>
                    <PageJumpInput
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onChange={handlePageChange}
                    />
                    <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage >= totalPages || loading}
                        className="pagination-btn"
                    >
                        Next
                    </button>
                    <button
                        onClick={() => handlePageChange(totalPages)}
                        disabled={currentPage >= totalPages || loading}
                        className="pagination-btn"
                    >
                        Last
                    </button>
                </div>
            </div>

            </div>{/* /nabers-results */}
            </div>{/* /nabers-body */}

            {/* ✅ SUCCESS/ERROR NOTIFICATION MODALS */}
            {modal.open && !modal.isError && (
                <div className="notification-modal-overlay" onClick={closeModal}>
                    <div className="notification-modal-content success-notification" onClick={(e) => e.stopPropagation()}>
                        <div className="notification-icon">{modal.icon}</div>
                        <h2 className="notification-title">{modal.title}</h2>
                        <p className="notification-message">{modal.message}</p>
                        <button className="notification-btn success-btn" onClick={closeModal}>OK</button>
                    </div>
                </div>
            )}

            {modal.open && modal.isError && (
                <div className="notification-modal-overlay" onClick={closeModal}>
                    <div className="notification-modal-content error-notification" onClick={(e) => e.stopPropagation()}>
                        <div className="notification-icon">{modal.icon}</div>
                        <h2 className="notification-title">{modal.title}</h2>
                        <p className="notification-message">{modal.message}</p>
                        <button className="notification-btn error-btn" onClick={closeModal}>Close</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NabersRefEfPage;
